import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import {
	AlertTriangle,
	ArrowLeft,
	Calendar,
	Car,
	Check,
	ChevronDown,
	ChevronRight,
	Circle,
	ClipboardCheck,
	Copy,
	Download,
	ImagePlus,
	Medal,
	Monitor,
	Pencil,
	Play,
	Plus,
	QrCode,
	ScrollText,
	Timer,
	Trophy,
	Tv,
	Vote,
	X,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import { getDb } from "@/db";
import { cars, events, heats as heatsTable } from "@/db/schema";
import { getSession } from "@/lib/auth-session";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

const getEventAdmin = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		const heatsResult = await db
			.select()
			.from(heatsTable)
			.where(eq(heatsTable.eventId, event.id));

		return {
			event,
			carCount: eventCars.length,
			checkedInCount: eventCars.filter((c) => c.checkedIn).length,
			heatCount: heatsResult.length,
		};
	});

const updateEvent = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			eventId: number;
			name: string;
			date: string;
			location: string | null;
			maxWeightOz: number;
			weightUnit: string;
			laneCount: number;
			scoringMethod: string;
			attemptsAllowed: number;
			rounds: number;
			divisions: string;
			trackLengthFt: number | null;
			visibility: string;
			registrationMode: string;
			carNumberMode: string;
			requireCheckin: boolean;
			finalsCarCount: number | null;
			finalsScoringMethod: string | null;
			finalsRounds: number;
			racesPerCar: number | null;
		}) => data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const validVisibility = ["public", "unlisted", "draft"];
		const validRegMode = ["open", "admin_only"];
		const validCarNumMode = ["racer_choice", "auto", "admin_only"];
		const validScoring = ["best", "average", "drop_worst"];
		const validWeightUnit = ["oz", "g"];
		if (!validVisibility.includes(data.visibility))
			throw new Error("Invalid visibility");
		if (!validRegMode.includes(data.registrationMode))
			throw new Error("Invalid registration mode");
		if (!validCarNumMode.includes(data.carNumberMode))
			throw new Error("Invalid car number mode");
		if (!validScoring.includes(data.scoringMethod))
			throw new Error("Invalid scoring method");
		if (!validWeightUnit.includes(data.weightUnit))
			throw new Error("Invalid weight unit");
		await getDb()
			.update(events)
			.set({
				name: data.name,
				date: data.date,
				location: data.location,
				maxWeightOz: data.maxWeightOz,
				weightUnit: data.weightUnit,
				laneCount: data.laneCount,
				scoringMethod: data.scoringMethod,
				attemptsAllowed: data.attemptsAllowed,
				rounds: data.rounds,
				divisions: data.divisions,
				trackLengthFt: data.trackLengthFt,
				visibility: data.visibility,
				registrationMode: data.registrationMode,
				carNumberMode: data.carNumberMode,
				requireCheckin: data.requireCheckin,
				finalsCarCount: data.finalsCarCount,
				finalsScoringMethod: data.finalsScoringMethod,
				finalsRounds: data.finalsRounds,
				racesPerCar: data.racesPerCar,
				updatedAt: new Date(),
			})
			.where(eq(events.id, data.eventId));
	});

const updateEventStatus = createServerFn({ method: "POST" })
	.inputValidator((data: { eventId: number; status: string }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		const validStatuses = ["setup", "qualifying", "finals", "complete"];
		if (!validStatuses.includes(data.status))
			throw new Error("Invalid status");
		await getDb()
			.update(events)
			.set({ status: data.status, updatedAt: new Date() })
			.where(eq(events.id, data.eventId));
	});

const deleteEvent = createServerFn({ method: "POST" })
	.inputValidator((eventId: number) => eventId)
	.handler(async ({ data: eventId }) => {
		await requireAdmin();
		await getDb().delete(events).where(eq(events.id, eventId));
	});

const cloneEvent = createServerFn({ method: "POST" })
	.inputValidator((eventId: number) => eventId)
	.handler(async ({ data: eventId }) => {
		await requireAdmin();
		const db = getDb();

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, eventId))
			.get();
		if (!event) throw new Error("Event not found");

		// Clone the event with a new name, reset status to setup
		const [newEvent] = await db
			.insert(events)
			.values({
				name: `${event.name} (Copy)`,
				date: event.date,
				location: event.location,
				maxWeightOz: event.maxWeightOz,
				weightUnit: event.weightUnit,
				laneCount: event.laneCount,
				scoringMethod: event.scoringMethod,
				attemptsAllowed: event.attemptsAllowed,
				rounds: event.rounds,
				divisions: event.divisions,
				imageUrl: event.imageUrl,
				trackLengthFt: event.trackLengthFt,
				finalsCarCount: event.finalsCarCount,
				finalsScoringMethod: event.finalsScoringMethod,
				finalsRounds: event.finalsRounds,
				racesPerCar: event.racesPerCar,
				visibility: event.visibility,
				registrationMode: event.registrationMode,
				carNumberMode: event.carNumberMode,
				requireCheckin: event.requireCheckin,
				status: "setup",
			})
			.returning();

		// Clone car registrations (without race data)
		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, eventId));

		for (const car of eventCars) {
			await db.insert(cars).values({
				eventId: newEvent.id,
				userId: car.userId,
				driverName: car.driverName,
				carName: car.carName,
				carNumber: car.carNumber,
				weightOz: car.weightOz,
				photoUrl: car.photoUrl,
				avatarUrl: car.avatarUrl,
				division: car.division,
				checkedIn: false,
			});
		}

		return newEvent.id;
	});

const updateEventImage = createServerFn({ method: "POST" })
	.inputValidator((data: { eventId: number; imageUrl: string }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(events)
			.set({ imageUrl: data.imageUrl, updatedAt: new Date() })
			.where(eq(events.id, data.eventId));
	});

export const Route = createFileRoute("/admin/events/$eventId/")({
	loader: ({ params }) => getEventAdmin({ data: params.eventId }),
	component: AdminEventPage,
});

function getStatusTransitions(finalsEnabled: boolean): Record<string, string[]> {
	return finalsEnabled
		? {
				setup: ["qualifying"],
				qualifying: ["finals", "complete"],
				finals: ["complete"],
				complete: [],
			}
		: {
				setup: ["qualifying"],
				qualifying: ["complete"],
				complete: [],
			};
}

function displayStatus(status: string, finalsEnabled: boolean): string {
	if (!finalsEnabled && status === "qualifying") return "racing";
	return status;
}

function statusBadgeClass(status: string) {
	switch (status) {
		case "setup":
			return "bg-status-setup text-foreground";
		case "qualifying":
		case "racing":
			return "bg-status-qualifying text-foreground";
		case "finals":
			return "bg-status-finals text-foreground";
		default:
			return "bg-status-complete text-foreground";
	}
}

function AdminEventPage() {
	const { event, carCount, checkedInCount, heatCount } = Route.useLoaderData();
	const navigate = useNavigate();
	const [confirm, confirmDialog] = useConfirm();
	const [statusLoading, setStatusLoading] = useState(false);
	const [editing, setEditing] = useState(false);
	const [editLoading, setEditLoading] = useState(false);
	const [imageLoading, setImageLoading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const formId = useId();
	const [divisions, setDivisions] = useState<string[]>(() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	});
	const [newDivision, setNewDivision] = useState("");
	const [showFinals, setShowFinals] = useState(
		() => event.finalsCarCount != null && event.finalsCarCount > 0,
	);

	const finalsEnabled =
		event.finalsCarCount != null && event.finalsCarCount > 0;
	const statusTransitions = getStatusTransitions(finalsEnabled);
	const nextStatuses = statusTransitions[event.status] || [];

	async function handleStatusChange(newStatus: string) {
		setStatusLoading(true);
		await updateEventStatus({ data: { eventId: event.id, status: newStatus } });
		navigate({
			to: "/admin/events/$eventId",
			params: { eventId: String(event.id) },
			reloadDocument: true,
		});
	}

	async function handleEdit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setEditLoading(true);
		const form = new FormData(e.currentTarget);
		const finalsCarCountStr = form.get("finalsCarCount") as string;
		const racesPerCarStr = form.get("racesPerCar") as string;
		await updateEvent({
			data: {
				eventId: event.id,
				name: form.get("name") as string,
				date: form.get("date") as string,
				location: (form.get("location") as string) || null,
				maxWeightOz: Number(form.get("maxWeightOz")),
				weightUnit: form.get("weightUnit") as string,
				laneCount: Number(form.get("laneCount")),
				scoringMethod: form.get("scoringMethod") as string,
				attemptsAllowed: Number(form.get("attemptsAllowed")),
				rounds: Number(form.get("rounds")),
				divisions: JSON.stringify(divisions),
				trackLengthFt: form.get("trackLengthFt")
					? Number(form.get("trackLengthFt"))
					: null,
				visibility: (form.get("visibility") as string) || "public",
				registrationMode: (form.get("registrationMode") as string) || "open",
				carNumberMode: (form.get("carNumberMode") as string) || "racer_choice",
				requireCheckin: form.get("requireCheckin") === "on",
				finalsCarCount: finalsCarCountStr ? Number(finalsCarCountStr) : null,
				finalsScoringMethod:
					(form.get("finalsScoringMethod") as string) || null,
				finalsRounds: Number(form.get("finalsRounds") || "1"),
				racesPerCar: racesPerCarStr ? Number(racesPerCarStr) : null,
			},
		});
		setEditLoading(false);
		setEditing(false);
		navigate({
			to: "/admin/events/$eventId",
			params: { eventId: String(event.id) },
			reloadDocument: true,
		});
	}

	async function handleClone() {
		const ok = await confirm({
			title: "Clone Event",
			description:
				"Clone this event? All settings and car registrations will be copied. Race data will not be included.",
			confirmLabel: "Clone",
		});
		if (!ok) return;
		const newEventId = await cloneEvent({ data: event.id });
		navigate({
			to: "/admin/events/$eventId",
			params: { eventId: String(newEventId) },
		});
	}

	async function handleDelete() {
		const ok = await confirm({
			title: "Delete Event",
			description:
				"Delete this event and all associated data? This cannot be undone.",
			confirmLabel: "Delete",
			variant: "destructive",
		});
		if (!ok) return;
		await deleteEvent({ data: event.id });
		navigate({ to: "/admin/events" });
	}

	async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;
		setImageLoading(true);
		const formData = new FormData();
		formData.append("file", file);
		formData.append("eventId", String(event.id));
		const res = await fetch("/api/upload", { method: "POST", body: formData });
		if (!res.ok) throw new Error("Upload failed");
		const { url } = (await res.json()) as { url: string };
		await updateEventImage({ data: { eventId: event.id, imageUrl: url } });
		setImageLoading(false);
		navigate({
			to: "/admin/events/$eventId",
			params: { eventId: String(event.id) },
			reloadDocument: true,
		});
	}

	return (
		<div className="min-h-screen bg-background p-6">
			{confirmDialog}
			<div className="max-w-3xl mx-auto">
				<Link
					to="/admin/events"
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					All Events
				</Link>

				<div className="flex items-start justify-between mb-8">
					<div>
						<h1 className="text-4xl text-foreground">{event.name}</h1>
						<p className="text-muted-foreground mt-1">
							{event.date}
							{event.location && ` - ${event.location}`}
						</p>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={handleClone}
							title="Create a copy of this event with all settings and car registrations (no race data)"
							className="inline-flex items-center gap-1 px-3 py-1.5 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
						>
							<Copy size={14} />
							Clone
						</button>
						<span
							className={`px-3 py-1 rounded-full text-sm font-bold uppercase ${statusBadgeClass(displayStatus(event.status, finalsEnabled))}`}
						>
							{displayStatus(event.status, finalsEnabled)}
						</span>
					</div>
				</div>

				{/* Pre-race checklist */}
				{(event.status === "setup" || event.status === "qualifying") && (
					<div className="bg-card rounded-xl p-4 sm:p-6 mb-6">
						<h2 className="text-sm font-bold text-muted-foreground uppercase mb-3">
							Race Day Checklist
						</h2>
						<div className="space-y-2">
							<ChecklistItem
								done={carCount > 0}
								label="Cars registered"
								detail={
									carCount > 0
										? `${carCount} car${carCount !== 1 ? "s" : ""}`
										: "No cars yet"
								}
							/>
							<ChecklistItem
								done={checkedInCount > 0}
								warn={checkedInCount > 0 && checkedInCount < carCount}
								label="Cars checked in"
								detail={
									checkedInCount > 0
										? `${checkedInCount} of ${carCount} checked in`
										: "None checked in"
								}
							/>
							<ChecklistItem
								done={heatCount > 0}
								label="Schedule generated"
								detail={
									heatCount > 0
										? `${heatCount} heat${heatCount !== 1 ? "s" : ""}`
										: "No schedule"
								}
							/>
						</div>
					</div>
				)}

				{/* Quick actions */}
				<div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
					<Link
						to="/admin/events/$eventId/cars"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Car size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Cars</p>
						<p className="text-muted-foreground text-sm">
							{carCount} registered
						</p>
					</Link>

					<Link
						to="/admin/events/$eventId/checkin"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<ClipboardCheck size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Check-In</p>
						<p className="text-muted-foreground text-sm">Weigh-in & confirm attendance</p>
					</Link>

					<Link
						to="/admin/events/$eventId/schedule"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Calendar size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Schedule</p>
						<p className="text-muted-foreground text-sm">Heat assignments</p>
					</Link>

					<Link
						to="/admin/events/$eventId/race"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Timer size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Race</p>
						<p className="text-muted-foreground text-sm">Enter timing data</p>
					</Link>

					<Link
						to="/admin/events/$eventId/awards"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Medal size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Awards</p>
						<p className="text-muted-foreground text-sm">Trophies & prizes</p>
					</Link>

					<Link
						to="/events/$eventId"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Trophy size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Leaderboard</p>
						<p className="text-muted-foreground text-sm">Public view</p>
					</Link>

					<Link
						to="/events/$eventId/staging"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Monitor size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">On-Deck</p>
						<p className="text-muted-foreground text-sm">Shows next heat to racers</p>
					</Link>

					<Link
						to="/events/$eventId/qr"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<QrCode size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">QR Codes</p>
						<p className="text-muted-foreground text-sm">Print for racers</p>
					</Link>

					<Link
						to="/events/$eventId/slideshow"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Play size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Slideshow</p>
						<p className="text-muted-foreground text-sm">Auto-rotating car photos</p>
					</Link>

					<Link
						to="/admin/events/$eventId/voting"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Vote size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Voting</p>
						<p className="text-muted-foreground text-sm">Design awards</p>
					</Link>

					<Link
						to="/admin/events/$eventId/tv-control"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<Tv size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">TV Control</p>
						<p className="text-muted-foreground text-sm">Control the TV display</p>
					</Link>

					<Link
						to="/admin/events/$eventId/audit"
						params={{ eventId: String(event.id) }}
						className="bg-card hover:border-cherry/40 rounded-xl p-5 text-center transition-all"
					>
						<ScrollText size={28} className="text-primary mx-auto mb-2" />
						<p className="text-foreground font-bold">Audit Log</p>
						<p className="text-muted-foreground text-sm">Activity history</p>
					</Link>
				</div>

				{/* Export */}
				<div className="bg-card rounded-xl p-6 mb-6">
					<h2 className="text-lg text-foreground mb-3">Export Results</h2>
					<div className="flex gap-3">
						<a
							href={`/api/export?eventId=${event.id}&type=standings`}
							download
							className="inline-flex items-center gap-1.5 px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold"
						>
							<Download size={14} />
							Standings CSV
						</a>
						<a
							href={`/api/export?eventId=${event.id}&type=runs`}
							download
							className="inline-flex items-center gap-1.5 px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold"
						>
							<Download size={14} />
							All Runs CSV
						</a>
					</div>
				</div>

				{/* Event image */}
				<div className="bg-card rounded-xl p-6 mb-6">
					<h2 className="text-lg text-foreground mb-4">Event Image</h2>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						className="hidden"
						onChange={handleImageUpload}
					/>
					{event.imageUrl ? (
						<div className="relative group">
							<img
								src={event.imageUrl}
								alt={event.name}
								className="w-full max-h-48 object-cover rounded-xl"
							/>
							<button
								type="button"
								onClick={() => fileInputRef.current?.click()}
								disabled={imageLoading}
								className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity"
							>
								<span className="px-4 py-2 bg-primary text-primary-foreground font-bold rounded-lg text-sm uppercase">
									{imageLoading ? "Uploading..." : "Change"}
								</span>
							</button>
						</div>
					) : (
						<button
							type="button"
							onClick={() => fileInputRef.current?.click()}
							disabled={imageLoading}
							className="w-full border-2 border-dashed border-border hover:border-cherry/40 rounded-xl p-8 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
						>
							<ImagePlus size={32} />
							<span className="text-sm font-bold">
								{imageLoading ? "Uploading..." : "Add Event Image"}
							</span>
						</button>
					)}
				</div>

				{/* Event settings */}
				<div className="bg-card rounded-xl p-6 mb-6">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg text-foreground">Settings</h2>
						{!editing && (
							<button
								type="button"
								onClick={() => setEditing(true)}
								className="inline-flex items-center gap-1 px-3 py-1.5 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
							>
								<Pencil size={14} />
								Edit
							</button>
						)}
					</div>

					{editing ? (
						<form onSubmit={handleEdit} className="space-y-4">
							<div>
								<label
									htmlFor={`${formId}-name`}
									className="block text-sm mb-1"
								>
									Event Name
								</label>
								<input
									id={`${formId}-name`}
									name="name"
									type="text"
									required
									defaultValue={event.name}
									className="w-full px-4 py-2"
								/>
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div>
									<label
										htmlFor={`${formId}-date`}
										className="block text-sm mb-1"
									>
										Date
									</label>
									<input
										id={`${formId}-date`}
										name="date"
										type="date"
										required
										defaultValue={event.date}
										className="w-full px-4 py-2"
									/>
								</div>
								<div>
									<label
										htmlFor={`${formId}-location`}
										className="block text-sm mb-1"
									>
										Location
									</label>
									<input
										id={`${formId}-location`}
										name="location"
										type="text"
										defaultValue={event.location || ""}
										className="w-full px-4 py-2"
									/>
								</div>
							</div>
							<div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
								<div>
									<label
										htmlFor={`${formId}-lanes`}
										className="block text-sm mb-1"
									>
										Lanes
									</label>
									<input
										id={`${formId}-lanes`}
										name="laneCount"
										type="number"
										min="1"
										max="8"
										defaultValue={event.laneCount}
										className="w-full px-4 py-2"
									/>
								</div>
								<div>
									<label
										htmlFor={`${formId}-weight`}
										className="block text-sm mb-1"
									>
										Max Weight
									</label>
									<div className="flex gap-2">
										<input
											id={`${formId}-weight`}
											name="maxWeightOz"
											type="number"
											step="0.1"
											min="0.1"
											defaultValue={event.maxWeightOz}
											className="flex-1 px-4 py-2"
										/>
										<select
											name="weightUnit"
											defaultValue={event.weightUnit}
											className="px-2 py-2"
										>
											<option value="oz">oz</option>
											<option value="g">g</option>
										</select>
									</div>
								</div>
								<div>
									<label
										htmlFor={`${formId}-scoring`}
										className="block text-sm mb-1"
									>
										Scoring
									</label>
									<select
										id={`${formId}-scoring`}
										name="scoringMethod"
										defaultValue={event.scoringMethod}
										className="w-full px-4 py-2"
									>
										<option value="best">Best Time</option>
										<option value="average">Average</option>
										<option value="drop_worst">Drop Worst</option>
										<option value="points">
											Points (1st=1pt, 2nd=2pts...)
										</option>
									</select>
									<p className="text-muted-foreground text-xs mt-1">
										How final score is calculated
									</p>
								</div>
								<div>
									<label
										htmlFor={`${formId}-attempts`}
										className="block text-sm mb-1"
										title="Total runs each car gets (typically lanes x rounds)"
									>
										Attempts
									</label>
									<input
										id={`${formId}-attempts`}
										name="attemptsAllowed"
										type="number"
										min="1"
										max="20"
										defaultValue={event.attemptsAllowed}
										className="w-full px-4 py-2"
									/>
								</div>
								<div>
									<label
										htmlFor={`${formId}-rounds`}
										className="block text-sm mb-1"
										title="Full rotations through all lanes per car"
									>
										Rounds
									</label>
									<select
										id={`${formId}-rounds`}
										name="rounds"
										defaultValue={event.rounds}
										className="w-full px-4 py-2"
									>
										<option value="1">1</option>
										<option value="2">2</option>
										<option value="3">3</option>
									</select>
								</div>
								<div>
									<label
										htmlFor={`${formId}-racesPerCar`}
										className="block text-sm mb-1"
									>
										Races per Car
									</label>
									<input
										id={`${formId}-racesPerCar`}
										name="racesPerCar"
										type="number"
										min="1"
										max="24"
										placeholder="All lanes"
										defaultValue={event.racesPerCar ?? ""}
										className="w-full px-4 py-2"
									/>
									<p className="text-muted-foreground text-xs mt-1">
										Leave blank for full rotation (every lane)
									</p>
								</div>
							</div>
							<div>
								<label
									htmlFor={`${formId}-trackLength`}
									className="block text-sm mb-1"
								>
									Track Length (ft)
								</label>
								<input
									id={`${formId}-trackLength`}
									name="trackLengthFt"
									type="number"
									step="0.1"
									min="0"
									placeholder="e.g., 32"
									defaultValue={event.trackLengthFt ?? ""}
									className="w-full sm:w-48 px-4 py-2"
								/>
								<p className="text-muted-foreground text-xs mt-1">
									Optional — enables speed (MPH) display
								</p>
							</div>
							{/* Divisions */}
							<div>
								<p className="text-sm mb-1">Divisions</p>
								<p className="text-muted-foreground text-xs mb-2">
									Separate leaderboards and schedules per group (e.g., age groups, dens)
								</p>
								<div className="flex flex-wrap gap-2 mb-2">
									{divisions.map((div) => (
										<span
											key={div}
											className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full"
										>
											{div}
											<button
												type="button"
												onClick={() =>
													setDivisions((d) => d.filter((x) => x !== div))
												}
												className="p-0.5 hover:bg-primary/20 rounded-full transition-colors"
											>
												<X size={12} />
											</button>
										</span>
									))}
								</div>
								<div className="flex gap-2">
									<input
										type="text"
										value={newDivision}
										onChange={(e) => setNewDivision(e.target.value)}
										placeholder="e.g., Tigers"
										className="flex-1 px-3 py-1.5 text-sm"
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												const val = newDivision.trim();
												if (val && !divisions.includes(val)) {
													setDivisions((d) => [...d, val]);
													setNewDivision("");
												}
											}
										}}
									/>
									<button
										type="button"
										onClick={() => {
											const val = newDivision.trim();
											if (val && !divisions.includes(val)) {
												setDivisions((d) => [...d, val]);
												setNewDivision("");
											}
										}}
										className="px-3 py-1.5 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg text-sm font-bold"
									>
										<Plus size={14} />
									</button>
								</div>
							</div>

							{/* Registration & Visibility */}
							<div className="border-t border-border pt-4">
								<p className="text-sm font-bold text-muted-foreground uppercase mb-3">
									Registration
								</p>
								<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
									<div>
										<label
											htmlFor={`${formId}-visibility`}
											className="block text-sm mb-1"
										>
											Visibility
										</label>
										<select
											id={`${formId}-visibility`}
											name="visibility"
											defaultValue={event.visibility}
											className="w-full px-4 py-2"
										>
											<option value="public">Public</option>
											<option value="unlisted">Unlisted (link only)</option>
											<option value="draft">Draft (admin only)</option>
										</select>
									</div>
									<div>
										<label
											htmlFor={`${formId}-regMode`}
											className="block text-sm mb-1"
										>
											Registration
										</label>
										<select
											id={`${formId}-regMode`}
											name="registrationMode"
											defaultValue={event.registrationMode}
											className="w-full px-4 py-2"
										>
											<option value="open">Open (self-register)</option>
											<option value="admin_only">Admin Only</option>
										</select>
									</div>
									<div>
										<label
											htmlFor={`${formId}-carNumMode`}
											className="block text-sm mb-1"
										>
											Car Numbers
										</label>
										<select
											id={`${formId}-carNumMode`}
											name="carNumberMode"
											defaultValue={event.carNumberMode}
											className="w-full px-4 py-2"
										>
											<option value="racer_choice">Racer's Choice</option>
											<option value="auto">Auto-Assign</option>
											<option value="admin_only">Admin Only</option>
										</select>
									</div>
								</div>
								<label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
									<input
										type="checkbox"
										name="requireCheckin"
										defaultChecked={event.requireCheckin}
										className="rounded"
									/>
									Require check-in before cars appear in schedule
								</label>
							</div>

							{/* Finals */}
							<div className="border-t border-border pt-4">
								<button
									type="button"
									onClick={() => setShowFinals(!showFinals)}
									className="flex items-center gap-2 text-sm font-bold text-muted-foreground uppercase hover:text-foreground transition-colors"
								>
									{showFinals ? (
										<ChevronDown size={14} />
									) : (
										<ChevronRight size={14} />
									)}
									Finals Round
								</button>
								{showFinals && (
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
										<div>
											<label
												htmlFor={`${formId}-finalsCount`}
												className="block text-sm mb-1"
											>
												Top N Cars
											</label>
											<input
												id={`${formId}-finalsCount`}
												name="finalsCarCount"
												type="number"
												min="2"
												max="20"
												defaultValue={event.finalsCarCount ?? 4}
												className="w-full px-4 py-2"
											/>
											<p className="text-muted-foreground text-xs mt-1">
												Cars advancing from qualifying
											</p>
										</div>
										<div>
											<label
												htmlFor={`${formId}-finalsScoring`}
												className="block text-sm mb-1"
											>
												Finals Scoring
											</label>
											<select
												id={`${formId}-finalsScoring`}
												name="finalsScoringMethod"
												defaultValue={event.finalsScoringMethod ?? ""}
												className="w-full px-4 py-2"
											>
												<option value="">Same as qualifying</option>
												<option value="best">Best Time</option>
												<option value="average">Average</option>
												<option value="points">Points</option>
											</select>
										</div>
										<div>
											<label
												htmlFor={`${formId}-finalsRounds`}
												className="block text-sm mb-1"
											>
												Finals Rounds
											</label>
											<select
												id={`${formId}-finalsRounds`}
												name="finalsRounds"
												defaultValue={event.finalsRounds}
												className="w-full px-4 py-2"
											>
												<option value="1">1</option>
												<option value="2">2</option>
												<option value="3">3</option>
											</select>
											<p className="text-muted-foreground text-xs mt-1">
												Full rotations through all lanes
											</p>
										</div>
									</div>
								)}
							</div>

							<div className="flex gap-3">
								<button
									type="submit"
									disabled={editLoading}
									className="px-6 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
								>
									{editLoading ? "Saving..." : "Save Changes"}
								</button>
								<button
									type="button"
									onClick={() => setEditing(false)}
									className="px-6 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
								>
									Cancel
								</button>
							</div>
						</form>
					) : (
						<>
							<div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Lanes
									</p>
									<p className="text-foreground text-lg">{event.laneCount}</p>
								</div>
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Max Weight
									</p>
									<p className="text-foreground text-lg">
										{event.maxWeightOz} {event.weightUnit}
									</p>
								</div>
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Scoring
									</p>
									<p className="text-foreground text-lg capitalize">
										{event.scoringMethod.replace("_", " ")}
									</p>
								</div>
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Attempts
									</p>
									<p className="text-foreground text-lg">
										{event.attemptsAllowed}
									</p>
								</div>
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Rounds
									</p>
									<p className="text-foreground text-lg">{event.rounds}</p>
								</div>
								{event.racesPerCar != null && (
									<div>
										<p className="text-muted-foreground font-bold uppercase text-xs">
											Races/Car
										</p>
										<p className="text-foreground text-lg">
											{event.racesPerCar}
										</p>
									</div>
								)}
							</div>
							{event.trackLengthFt && (
								<div className="mt-4 pt-4 border-t border-border">
									<p className="text-muted-foreground font-bold uppercase text-xs mb-1">
										Track Length
									</p>
									<p className="text-foreground text-lg">
										{event.trackLengthFt} ft
									</p>
								</div>
							)}
							{divisions.length > 0 && (
								<div className="mt-4 pt-4 border-t border-border">
									<p className="text-muted-foreground font-bold uppercase text-xs mb-2">
										Divisions
									</p>
									<div className="flex flex-wrap gap-2">
										{divisions.map((div) => (
											<span
												key={div}
												className="px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full"
											>
												{div}
											</span>
										))}
									</div>
								</div>
							)}
							{/* Registration settings display */}
							<div className="mt-4 pt-4 border-t border-border">
								<div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
									<div>
										<p className="text-muted-foreground font-bold uppercase text-xs">
											Visibility
										</p>
										<p className="text-foreground text-lg capitalize">
											{event.visibility}
										</p>
									</div>
									<div>
										<p className="text-muted-foreground font-bold uppercase text-xs">
											Registration
										</p>
										<p className="text-foreground text-lg">
											{event.registrationMode === "open"
												? "Open"
												: "Admin Only"}
										</p>
									</div>
									<div>
										<p className="text-muted-foreground font-bold uppercase text-xs">
											Car Numbers
										</p>
										<p className="text-foreground text-lg">
											{event.carNumberMode === "racer_choice"
												? "Racer's Choice"
												: event.carNumberMode === "auto"
													? "Auto"
													: "Admin Only"}
										</p>
									</div>
									<div>
										<p className="text-muted-foreground font-bold uppercase text-xs">
											Check-in
										</p>
										<p className="text-foreground text-lg">
											{event.requireCheckin ? "Required" : "Optional"}
										</p>
									</div>
								</div>
							</div>
							{event.finalsCarCount != null && event.finalsCarCount > 0 && (
								<div className="mt-4 pt-4 border-t border-border">
									<p className="text-muted-foreground font-bold uppercase text-xs mb-2">
										Finals
									</p>
									<div className="grid grid-cols-3 gap-4 text-sm">
										<div>
											<p className="text-muted-foreground font-bold uppercase text-xs">
												Top Cars
											</p>
											<p className="text-foreground text-lg">
												{event.finalsCarCount}
											</p>
										</div>
										<div>
											<p className="text-muted-foreground font-bold uppercase text-xs">
												Scoring
											</p>
											<p className="text-foreground text-lg capitalize">
												{event.finalsScoringMethod
													? event.finalsScoringMethod.replace("_", " ")
													: "Same as qualifying"}
											</p>
										</div>
										<div>
											<p className="text-muted-foreground font-bold uppercase text-xs">
												Rounds
											</p>
											<p className="text-foreground text-lg">
												{event.finalsRounds}
											</p>
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</div>

				{/* Status transitions with guardrails */}
				{nextStatuses.length > 0 && (
					<StatusTransitionPanel
						event={event}
						nextStatuses={nextStatuses}
						statusLoading={statusLoading}
						onStatusChange={handleStatusChange}
						carCount={carCount}
						checkedInCount={checkedInCount}
						heatCount={heatCount}
						finalsEnabled={finalsEnabled}
					/>
				)}

				{/* Danger zone */}
				{event.status === "setup" && (
					<div className="bg-destructive/10 border border-destructive/50 rounded-xl p-6">
						<h2 className="text-lg text-destructive mb-2">Danger Zone</h2>
						<p className="text-muted-foreground text-sm mb-4">
							Permanently delete this event and all its data.
						</p>
						<button
							type="button"
							onClick={handleDelete}
							className="px-4 py-2 bg-destructive hover:bg-destructive/80 text-foreground font-bold rounded-lg transition-colors uppercase text-sm"
						>
							Delete Event
						</button>
					</div>
				)}
			</div>
		</div>
	);
}

function ChecklistItem({
	done,
	warn,
	label,
	detail,
}: {
	done: boolean;
	warn?: boolean;
	label: string;
	detail: string;
}) {
	return (
		<div className="flex items-center gap-3 text-sm">
			{done ? (
				warn ? (
					<AlertTriangle size={16} className="text-status-finals shrink-0" />
				) : (
					<Check size={16} className="text-success shrink-0" />
				)
			) : (
				<Circle size={16} className="text-muted-foreground shrink-0" />
			)}
			<span className={done ? "text-foreground" : "text-muted-foreground"}>
				{label}
			</span>
			<span className="text-muted-foreground text-xs ml-auto">{detail}</span>
		</div>
	);
}

function StatusTransitionPanel({
	event,
	nextStatuses,
	statusLoading,
	onStatusChange,
	carCount,
	checkedInCount,
	heatCount,
	finalsEnabled,
}: {
	event: { id: number; status: string };
	nextStatuses: string[];
	statusLoading: boolean;
	onStatusChange: (status: string) => void;
	carCount: number;
	checkedInCount: number;
	heatCount: number;
	finalsEnabled: boolean;
}) {
	const [confirming, setConfirming] = useState<string | null>(null);

	function getWarnings(targetStatus: string): string[] {
		const warnings: string[] = [];
		if (targetStatus === "qualifying") {
			if (heatCount === 0) warnings.push("No schedule has been generated");
			if (checkedInCount === 0 && carCount > 0)
				warnings.push("No cars have been checked in");
		}
		if (targetStatus === "finals") {
			if (heatCount === 0) warnings.push("No heats exist");
		}
		return warnings;
	}

	function getDescription(targetStatus: string): string {
		switch (targetStatus) {
			case "qualifying":
				return finalsEnabled
					? "Opens qualifying races. Registration will remain open."
					: "Opens racing. Registration will remain open.";
			case "finals":
				return "Closes qualifying and begins finals racing.";
			case "complete":
				if (finalsEnabled && event.status === "qualifying") {
					return "Skips finals and marks the event as finished.";
				}
				return "Marks the event as finished. No more races can be run.";
			default:
				return "";
		}
	}

	function displayButtonLabel(status: string): string {
		return displayStatus(status, finalsEnabled);
	}

	return (
		<div className="bg-card rounded-xl p-6 mb-6">
			<h2 className="text-lg text-foreground mb-4">Status</h2>
			<p className="text-muted-foreground text-sm mb-4">
				Current:{" "}
				<strong className="text-foreground capitalize">
					{displayStatus(event.status, finalsEnabled)}
				</strong>
			</p>
			<div className="flex gap-3">
				{nextStatuses.map((status) => (
					<button
						type="button"
						key={status}
						onClick={() => setConfirming(status)}
						disabled={statusLoading}
						className="px-4 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
					>
						Move to {displayButtonLabel(status)}
					</button>
				))}
			</div>

			{confirming && (
				<div className="mt-4 p-4 bg-background border border-border rounded-lg">
					<p className="text-foreground font-bold mb-1">
						Move to{" "}
						<span className="capitalize">
							{displayButtonLabel(confirming)}
						</span>
						?
					</p>
					<p className="text-muted-foreground text-sm mb-3">
						{getDescription(confirming)}
					</p>
					{getWarnings(confirming).length > 0 && (
						<div className="bg-status-finals/10 border border-status-finals/30 rounded-lg p-3 mb-3">
							{getWarnings(confirming).map((w) => (
								<p
									key={w}
									className="text-status-finals text-sm flex items-center gap-1.5"
								>
									<AlertTriangle size={14} />
									{w}
								</p>
							))}
						</div>
					)}
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => {
								onStatusChange(confirming);
								setConfirming(null);
							}}
							disabled={statusLoading}
							className="px-4 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors text-sm"
						>
							{statusLoading ? "Updating..." : "Confirm"}
						</button>
						<button
							type="button"
							onClick={() => setConfirming(null)}
							className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
						>
							Cancel
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
